import { useTranslation } from "react-i18next";
import React, { useState, useCallback } from 'react';
import { connect } from 'react-redux';
import { Button, Row, Col } from 'reactstrap';
import { setProfileProperty } from 'store/slices/personalSlice';
import PropertyForm from 'Components/Personal/Property/PropertyForm';
import DeleteProperty from 'Components/Personal/Property/propertyForm/DeleteProperty';
import PropertyValue from 'Components/Personal/Property/PropertyValue';
import { contactMetadata } from 'Modules/ContactModule';
import { Link } from 'react-router-dom';

const PropertyValueWithControl = ({
  propertyMeta,
  value,
  setProfileProperty
}) => {
  const {
    t
  } = useTranslation();
  const [showForm, setShowForm] = useState(false);

  const initValue = () => {
    switch (propertyMeta.name) {
      case 'howisaymyname':
        return value.value;

      case 'birthday':
        return value.value;

      case 'phoneNumbers':
        // Add default phone type value if it is not specified
        return Object.assign({}, {
          3: { ...contactMetadata.phoneNumbers.fields.type,
            value: 'mobile'
          }
        }, value.value?.parameters || value.parameters);

      case 'postalAddresses':
        // Add default address type value if it is not specified
        return Object.assign({}, {
          6: { ...contactMetadata.postalAddresses.fields.type,
            value: 'home'
          }
        }, value.value?.parameters || value.parameters);

      case 'emailAddresses':
        // Handle email addresses - convert array format to numbered key format
        let emailParams = value.value?.parameters || value.parameters || value.value;

        if (Array.isArray(emailParams)) {
          // Convert array format [{"name": "address", "value": "..."}, {"name": "label", "value": "..."}] 
          // to numbered key format {"1": {"name": "address", "value": "..."}, "2": {"name": "type", "value": "..."}}
          const converted = {};
          emailParams.forEach((item, index) => {
            converted[index + 1] = item;
          });
          emailParams = converted;
        } // Add default email type value if it is not specified


        return Object.assign({}, {
          2: { ...contactMetadata.emailAddresses.fields.type,
            value: 'work'
          }
        }, emailParams);

      case 'urlAddresses':
        // Add default URL type value if it is not specified
        return Object.assign({}, {
          2: { ...contactMetadata.urlAddresses.fields.type,
            value: 'website'
          }
        }, value.value?.parameters || value.parameters);

      case 'onmymind':
        console.log('PropertyValueWithControl initValue onmymind - value:', value, 'type:', typeof value); // Handle onmymind property - extract the text value from parameters

        if (value.parameters && value.parameters['1'] && value.parameters['1'].value) {
          console.log('PropertyValueWithControl initValue onmymind - extracted from parameters:', value.parameters['1'].value);
          return value.parameters['1'].value;
        } else if (value.value && typeof value.value === 'string') {
          console.log('PropertyValueWithControl initValue onmymind - extracted from value.value:', value.value);
          return value.value;
        } else if (value.value && typeof value.value === 'object' && value.value.value) {
          console.log('PropertyValueWithControl initValue onmymind - extracted from value.value.value:', value.value.value);
          return value.value.value;
        }

        console.log('PropertyValueWithControl initValue onmymind - returning empty string');
        return '';

      case 'prefix':
      case 'pronoun':
      case 'thumbnailImage':
      case 'sexualOrientation':
        // Handle simple string properties - return the string value directly
        return value.value || '';

      default:
        return propertyMeta.fields ? value.value?.parameters || value.parameters : value.value;
    }
  };

  const closeForm = useCallback(() => setShowForm(false), []);
  const onSubmit = useCallback((name, localValue) => setProfileProperty(name, localValue, value.objectId).then(input => {
    setShowForm(false);
    return input;
  }), [setProfileProperty, value.objectId]); // Special handling for thumbnail image

  if (propertyMeta.name === 'thumbnailImage') {
    return <div>
        {!showForm ? <Row>
            <Col xs='12' md='3'>{propertyMeta.showName}</Col>
            <Col xs='8' md='7'><PropertyValue propertyMeta={propertyMeta} value={value} /></Col>
            <Col xs='4' md='2'>
              <Button color='link' onClick={() => setShowForm(true)}>{t("(Edit)")}</Button>
              <DeleteProperty value={value} propertyMeta={propertyMeta} />
            </Col>
          </Row> : <PropertyForm isThumbnail initValue={initValue()} propertyId={value?.objectId} propertyMeta={propertyMeta} property={propertyMeta} onSubmit={onSubmit} closeForm={closeForm} />}
      </div>;
  } // Special handling for howisaymyname (audio)


  if (propertyMeta.name === 'howisaymyname') {
    return <div>
        {!showForm ? <div>
            <p className='mb-1'>{propertyMeta.showName}</p>
            <div>
              <PropertyValue propertyMeta={propertyMeta} value={value} />
            </div>
            <Button color='link' onClick={() => setShowForm(true)}>{t("(Edit)")}</Button>
            {propertyMeta.multiple && <DeleteProperty value={value} propertyMeta={propertyMeta} />}
          </div> : <PropertyForm initValue={initValue()} property={propertyMeta} onSubmit={onSubmit} closeForm={closeForm} />}
      </div>;
  } // Default property display with edit/delete controls


  return <div>
      {!showForm ? <>
          <Row className='d-flex justify-content-between'>
            <Col xs='12' md='3'>
              {propertyMeta.showName}
              {propertyMeta.verifiable && <div>
                  <div>
                    <small className={value.verified ? 'text-primary' : 'text-danger'}>
                      {value.verified ? 'Verified' : 'Not verified'}
                    </small>
                  </div>
                  {!value.verified && <Link to={`/personal/property/${value.objectId}`} className='badge badge-primary'>{t("Verify")}</Link>}
                </div>}
            </Col>
            <Col xs='8' md='7'>
              <PropertyValue propertyMeta={propertyMeta} value={value} />
            </Col>
            <Col xs='4' md='2'>
              <Button color='link' onClick={() => setShowForm(true)}>{t("(Edit)")}</Button>
              {propertyMeta.name === 'familyName' || propertyMeta.name === 'givenName' ? null : <DeleteProperty value={value} propertyMeta={propertyMeta} />}
            </Col>
          </Row>
        </> : <PropertyForm initValue={initValue()} property={propertyMeta} onSubmit={onSubmit} closeForm={closeForm} />}
    </div>;
};

const mapDispatchToProps = dispatch => ({
  setProfileProperty: (name, value, id) => dispatch(setProfileProperty(name, value, id))
});

export default connect(null, mapDispatchToProps)(PropertyValueWithControl);